/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useMemo } from "react";
import { useAuthStore } from "src/store/account";
import PagePreloader from "src/components/loaders";
import { useBooleanState } from "src/hooks/boolean";
import { useTranslation } from "react-i18next";
import Col from "react-bootstrap/esm/Col";
import { Button } from "src/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import Text from "src/components/ui/text";
import { useProfileHasAnyPermission } from "src/hooks/profile";
import { MembershipPermission } from "src/enums/permission";
import ErrorBoundary from "src/pages/wrappers/ErrorBoundary";
import Divider from "src/components/ui/divider";
import BookCall from "src/components/book-call";
import SocialLogin from "src/components/social-login";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "src/components/ui/dialog";
import { useBreakpointIsLowerOrEqual } from "src/hooks/responsive";
import RegistrationForm from "src/components/forms/registration/RegistrationForm";
import { useNextPathStore } from "src/store/redirect-context";
import { LoginPopup, SignUpPopup } from "src/components/auth";

interface IPopupModalProps {
  isModalShow: boolean;
  closeModal: () => void;
}

interface IDemoWrapper {
  children: JSX.Element;
  permission: MembershipPermission | MembershipPermission[];
  demoComponent?: JSX.Element;
  popupModal?: (props: IPopupModalProps) => JSX.Element;
}

const DemoWrapper: React.FC<IDemoWrapper> = ({ children, demoComponent, permission, popupModal }) => {
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = React.useState(false);
  const { state: isModalShow, setTrue: showModal, setFalse: closeModal } = useBooleanState(false);
  const location = useLocation();
  const { setNextPath, nextPath } = useNextPathStore();

  React.useEffect(() => {
    if (isAuthenticated) {
      closeModal();
    }
  }, [isAuthenticated]);

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.bubbles = false;
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    setNextPath(currentUrl);
    showModal();
    return false;
  };

  const canAccessFeature = useProfileHasAnyPermission(permission);

  const defaultPopupModal = useMemo(
    () => (
      <DialogContent className="p-4 md:p-6 overflow-y-auto overflow-x-hidden flex max-h-screen max-w-full flex-col md:min-w-[400px] md:max-w-[450px] w-full min-h-screen md:min-h-fit">
        <DialogTitle>{t("Oops! This section is not available to your Advanced Tier.")}</DialogTitle>
        <DialogDescription className="sr-only" />
        <div className="flex-1">
          <Col xs={{ span: 22, offset: 1 }}>
            <Text fontSize="7" alignment="start" style={{ marginBottom: 16 }}>
              {t("Upgrade your subscription today to get nationwide access to all our features:")}
            </Text>
            <ul className="av-list av-list--small">
              <li className="av-list__item">{t("Evaluate thousands of properties at once with automated underwriting and evaluation")}</li>
              <li className="av-list__item">{t("Customize and edit financial analysis for any property")}</li>
              <li className="av-list__item">{t("Explore deals across hundreds of markets, all in one place")}</li>
              <li className="av-list__item">{t("Sort and filter properties to view opportunities relevant your investment criteria")}</li>
              <li className="av-list__item">{t("Receive automatic alerts for deals that match your investment criteria")}</li>
              <li className="av-list__item">{t("Explore hundreds of new properties every day")}</li>
            </ul>
          </Col>
        </div>
        <DialogFooter className="flex !flex-col pb-0">
          <Link className="w-100 d-block" to="/membership/select/">
            <Button className="w-100">{t("Upgrade tier")}</Button>
          </Link>
          <Divider className="w-100" text={t("or") as string} />
          <BookCall className="w-100 !text-foreground" buttonVariant="outline" buttonText={t("Speak with sales")} />
        </DialogFooter>
      </DialogContent>
    ),
    [t],
  );

  const demoForm = useMemo(() => (isLogin ? <LoginPopup setIsLogin={setIsLogin} /> : <SignUpPopup setIsLogin={setIsLogin} />), [isLogin]);

  const popupModalComponent = useMemo(
    () => (popupModal ? popupModal({ isModalShow, closeModal }) : defaultPopupModal),
    [popupModal, isModalShow, closeModal, defaultPopupModal],
  );

  const renderContent = useMemo(() => {
    if (!canAccessFeature) {
      return (
        <div style={{ position: "relative" }}>
          {demoComponent || (
            <div className="px-5 text-center">
              <h3 className="mb-5">
                {isAuthenticated
                  ? t("Oops! This feature is not available to your membership level.")
                  : t("Oops! This feature is only available for Airvana members.")}
              </h3>
              {isAuthenticated ? (
                <Link to="/membership/select/" className="text-decoration-none">
                  <Button>{t("Upgrade your membership")}</Button>
                </Link>
              ) : (
                <Link to="/sign-up/" className="text-decoration-none">
                  <Button>{t("Sign up for free")}</Button>
                </Link>
              )}
            </div>
          )}
          <a
            onClick={handleClick}
            tabIndex={-1}
            title=""
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 100,
            }}
          />
        </div>
      );
    }
    return <ErrorBoundary>{children}</ErrorBoundary>;
  }, [canAccessFeature, demoComponent, isAuthenticated, t, handleClick, children]);

  return (
    <React.Suspense fallback={<PagePreloader />}>
      {renderContent}
      {!canAccessFeature && (
        <Dialog open={isModalShow && !(isAuthenticated && nextPath)} onOpenChange={closeModal}>
          {isAuthenticated ? popupModalComponent : demoForm}
        </Dialog>
      )}
    </React.Suspense>
  );
};

export default DemoWrapper;
